import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { getCookie } from "../utils/cookie";
import { getRefreshToken, clearAuthCookies } from "./session";
import { decodeToken } from "../utils/jwt";
import { logout } from "../redux/slices/auth/authSlice";

const WARNING_BEFORE_MS = 300 * 1000;
const CHECK_INTERVAL_MS = 1 * 1000;

const SessionManager = ({ children }) => {
  const dispatch = useDispatch();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const refreshingRef = useRef(false);
  const warnedRef = useRef(false);
  const rootRef = useRef(null);

  const tryRefresh = useCallback(async () => {
    if (refreshingRef.current) return null;
    refreshingRef.current = true;
    try {
      const newToken = await getRefreshToken();
      if (newToken) {
        warnedRef.current = false;
        setShowWarning(false);
        return newToken;
      }
      return null;
    } catch {
      return null;
    } finally {
      refreshingRef.current = false;
    }
  }, []);

  useEffect(() => {
    const rootElement = rootRef.current || document.getElementById("root");
    if (!rootElement) return;

    const updateActivity = () => {
      lastActivityRef.current = Date.now();
      localStorage.setItem("lastActivity", lastActivityRef.current.toString());
    };

    const events = ["click", "dblclick", "contextmenu", "keydown"];
    events.forEach((e) => rootElement.addEventListener(e, updateActivity));

    return () => {
      events.forEach((e) => rootElement.removeEventListener(e, updateActivity));
    };
  }, []);

  useEffect(() => {
    const check = async () => {
      const accessToken = getCookie("accessToken");
      if (!accessToken) {
        setShowWarning(false);
        warnedRef.current = false;
        return;
      }

      try {
        const decoded = decodeToken(accessToken);
        if (!decoded || !decoded.exp) return;

        const timeRemaining = decoded.exp * 1000 - Date.now();

        if (timeRemaining <= 0) {
          if (refreshingRef.current) return;
          const refreshed = await tryRefresh();
          if (!refreshed) {
            clearAuthCookies();
            dispatch(logout());
          }
          return;
        }

        if (timeRemaining <= WARNING_BEFORE_MS) {
          setTimeLeft(Math.ceil(timeRemaining / 1000));
          setShowWarning(true);

          if (!refreshingRef.current) {
            const refreshed = await tryRefresh();
            if (!refreshed && timeRemaining <= 0) {
              clearAuthCookies();
              dispatch(logout());
            }
          }
        } else {
          setShowWarning(false);
          warnedRef.current = false;
        }
      } catch (error) {
        console.error("Session check failed:", error);
      }
    };

    check();
    const interval = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [tryRefresh]);

  const handleStay = async () => {
    lastActivityRef.current = Date.now();
    localStorage.setItem("lastActivity", lastActivityRef.current.toString());
    await tryRefresh();
  };

  return (
    <div ref={rootRef} style={{ minHeight: "100vh" }}>
      {children}
      {showWarning && (
        <div
          style={{
            position: "fixed",
            top: "16px",
            right: "16px",
            zIndex: 9999,
            backgroundColor: "#1e293b",
            color: "#f1f5f9",
            padding: "14px 20px",
            borderRadius: "10px",
            boxShadow: "0 8px 30px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            fontSize: "0.85rem",
            maxWidth: "400px",
            animation: "sessionSlideIn 0.3s ease-out",
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#facc15"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0 }}
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, marginBottom: "2px" }}>
              Session expiring in {timeLeft}s
            </div>
            <div style={{ fontSize: "0.75rem", opacity: 0.7 }}>
              Click anywhere or press a key to stay logged in
            </div>
          </div>
          <button
            onClick={handleStay}
            style={{
              background: "none",
              border: "1px solid rgba(255,255,255,0.25)",
              color: "#f1f5f9",
              padding: "6px 14px",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "0.8rem",
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            Stay
          </button>
        </div>
      )}
      <style>{`@keyframes sessionSlideIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }`}</style>
    </div>
  );
};

export default SessionManager;
